import { SignUpPopup, useNewsletterApi } from "@product/scmp-sdk";
import { useLocalStorageValue, useSyncedRef, useToggle } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import type { FunctionComponent } from "react";
import { useCallback, useEffect } from "react";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import type { DataMatrix } from "~/components/newsletter/newsletter-widget-container/types";
import { accountAtom } from "~/lib/account/atoms";
import { useRouterReplaceWithSanitizedKeys } from "~/lib/router/hooks";

import {
  SanitizedUrlKeys,
  SignUpCompleteUrlKey,
  SignUpMatrixV1LocalStorageKey,
  SubscriptionComboLocalStorageKey,
} from "./consts";
import { useNeedToShowNewsletterSignUpRedirect, useNewsletterTracking } from "./hooks";
import type { NewsletterPayload } from "./types";

export const SignUpActionRedirect: FunctionComponent = () => {
  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);

  const { handleSignUpNewsletter, isDataReady } = useNewsletterApi();
  const { handleNewsletterSuccessTracking } = useNewsletterTracking();
  const latestHandleSignUpNewsletter = useSyncedRef(handleSignUpNewsletter);
  const { asPath, pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const [showPopup, setShowPopup] = useToggle(false);

  const { value: dataMatrix } = useLocalStorageValue<DataMatrix>(SignUpMatrixV1LocalStorageKey);
  const { value: isCombo } = useLocalStorageValue<boolean>(SubscriptionComboLocalStorageKey);

  const execute = useCallback(
    async (parsedQueryString: NewsletterPayload, useremail: string) => {
      const response = await latestHandleSignUpNewsletter.current(
        parsedQueryString.newsletterIds,
        useremail,
      );
      handleNewsletterSuccessTracking({
        eventCategory: dataMatrix?.metrics?.event_category,
        isCombo: isCombo ?? false,
        isHongKong25: dataMatrix?.metrics?.isHongKong25,
        payload: parsedQueryString,
        subscriptions: response.entity?.subscriptions?.map(element => element?.entity) ?? [],
      });

      if (!response.errors?.length) {
        setShowPopup(true);
      }
    },
    [
      dataMatrix?.metrics?.event_category,
      dataMatrix?.metrics?.isHongKong25,
      handleNewsletterSuccessTracking,
      isCombo,
      latestHandleSignUpNewsletter,
      setShowPopup,
    ],
  );

  const needToShow = useNeedToShowNewsletterSignUpRedirect();

  useEffect(() => {
    const parsedQueryString = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    }) as unknown as NewsletterPayload;

    if (!needToShow || !parsedQueryString) return;
    if (!user?.uuid) {
      openLoginDialog({ destination: window.location.href });

      void replace(
        SanitizedUrlKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );
      return;
    }

    if (!isDataReady) return;
    void execute(parsedQueryString, user.useremail);
  }, [asPath, isDataReady, execute, openLoginDialog, pathname, query, replace, user, needToShow]);

  return (
    <SignUpPopup
      isShow={showPopup}
      onClose={() => {
        setShowPopup(false);
        // Move the replace to here is because we still need the query string to toggle the visibility of this component
        void replace(
          SanitizedUrlKeys,
          { pathname, query },
          {
            shallow: true,
          },
          {
            [SignUpCompleteUrlKey]: "true",
          },
        );
      }}
    />
  );
};

SignUpActionRedirect.displayName = "SignUpActionRedirect";
